import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Stack,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from "@mui/material";
import { isUndefined } from "lodash";
import { RootState } from "@/store/type";
import { useSelector } from "react-redux";
import { addToCart } from "@/store/authSlice";
import { useDispatch } from "react-redux";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { ROLE } from "@/types";
import { showToast } from "@/app/global-components/toaster";
import useItems from "@/hooks/use-item";
import useLobby from "@/hooks/use-lobby";
import { BiCartAdd } from "react-icons/bi";
import { EyeIcon } from "lucide-react";
import { FaSpinner } from "react-icons/fa";

interface Product {
  productId: string;
  unitName: string;
  SKUCode: string;
  quantity: number;
  unitPrice: number;
  vat: number;
  notice: string;
  attachment?: File | null;
  image?: string | null | undefined;
  company: {
    id: string;
    nameAr: string;
    CompanyNr: string;
  };
  tender: { title: string; id: string; status: string };
}

interface TenderQuotation {
  tender: { title: string; id: string; status: string };
  id: string;
  status: string;
  products: Product[];
  company: {
    CompanyNr: string;
    id: string;
    nameAr: string;
  };
}

interface QuatationsStatisticsProps {
  offerID?: string;
}

const MarkteComponent: React.FC<QuatationsStatisticsProps> = ({ offerID }) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const {
    getListtenderQuotationsByOfferId,
    getListtenderQuotationsByOfferIdMy,
  } = useLobby();
  const [listOffers, setListOffers] = useState<TenderQuotation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const userRole = isUndefined(authUser?.role) ? "" : authUser?.role;
  const { fetchItem } = useItems();
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchQuotations = async () => {
      if (offerID) {
        try {
          setIsLoading(true);
          if (userRole === ROLE.BUYER) {
            const Total = await getListtenderQuotationsByOfferId(offerID);
            if (Total) {
              setListOffers(Total.data);
              setIsLoading(false);
            } else {
              setListOffers([]);
              setIsLoading(false);
            }
          } else {
            const Total = await getListtenderQuotationsByOfferIdMy(offerID);
            if (Total) {
              setListOffers(Total.data);
            } else {
              setListOffers([]);
            }
          }
        } catch (error) {
          setIsLoading(false);
          console.error("Error fetching Offers:", error);
        }
      }
    };
    fetchQuotations();
  }, [offerID]);

  const handleAddToCart = async (product: Product, offer: TenderQuotation) => {
    try {
      const itemDetails = await fetchItem(product.productId); // Fetch full item details
      if (itemDetails?.isSuccess) {
        const fullProduct = {
          ...itemDetails.data,
          quantity: product.quantity,
          unitName: product.unitName,
        };
        const productToAdd = {
          productId: product.productId,
          unitName: product.unitName,
          SKUCode: product.SKUCode,
          quantity: product.quantity,
          unitPrice: product.unitPrice,
          vat: product.vat,
          notice: product.notice,
          image: product.image,
          company: offer.company,
          tender: offer.tender,
          quatationId: offer.id,
        };

        dispatch(addToCart(productToAdd));
        //setCart((prevCart) => [...prevCart, fullProduct]);
        showToast(
          `${fullProduct.unitName} ${t("Alert.AddedToCart")}`,
          "warning"
        );
      } else {
        showToast(t("Alert.ProductFetchError"), "warning");
      }
    } catch (error) {
      console.error("Error fetching item details:", error);
      showToast(t("Alert.CartAddError"), "warning");
    }
  };

  const handleShowItem = (product: Product, offer: TenderQuotation) => {
    setSelectedProduct({
      ...product,
      company: offer.company,
      tender: offer.tender,
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedProduct(null);
  };
  console.log(isLoading);
  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t("offers.allProducts")}
      </Typography>

      {isLoading ? (
        <div className="mx-auto w-full flex items-center justify-center mt-20">
          <FaSpinner />
        </div>
      ) : (
        <Stack
          flexWrap={"wrap"}
          sx={{ mt: 5 }}
          justifyContent={"center"}
          flexDirection={"row"}
          gap={5}
        >
          {listOffers
            .filter((offer) => offer.status !== "Canceled")
            .map((offer) =>
              offer.products.map((product) => (
                <Stack flexWrap={"wrap"} key={product.SKUCode}>
                  <Card
                    sx={{ border: "1px solid #e6e6e6" }}
                    component={Paper}
                    className="!rounded-2xl relative w-64 h-96"
                    elevation={0}
                  >
                    {product.image ? (
                      <CardMedia
                        component="img"
                        className="!h-64"
                        image={product.image ? product.image : ""}
                        alt={product.unitName}
                      />
                    ) : (
                      <CardMedia
                        component="div"
                        sx={{
                          height: 140,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#f0f0f0",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {t("images.noImage")}
                        </Typography>
                      </CardMedia>
                    )}
                    <CardContent>
                      <Typography
                        variant="h6"
                        className="!text-xl !font-semibold"
                        component="div"
                        gutterBottom
                      >
                        {product.unitName}
                      </Typography>

                      <Typography className="absolute top-2 !font-bold left-2">
                        {t("checkout.fields.qtn")}: {product.quantity}
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        <strong>{t("NR.Supplier")}:</strong>{" "}
                        {offer.company.CompanyNr}
                      </Typography>
                      <div className="flex justify-between">
                        <Button
                          variant="contained"
                          color="primary"
                          className="!fit-w max-w-fit"
                          fullWidth
                          sx={{
                            mt: 1,
                            borderColor: "#000",
                            backgroundColor: "#000",
                            "&:hover": {
                              borderColor: "#333",
                            },
                          }}
                          onClick={() => handleAddToCart(product, offer)}
                        >
                          <BiCartAdd className="text-2xl" />
                        </Button>
                        <Button
                          className="!fit-w max-w-fit "
                          sx={{ mt: 1, border: "1px solid #e6e6e6" }}
                          onClick={() => handleShowItem(product, offer)}
                        >
                          <EyeIcon />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Stack>
              ))
            )}
        </Stack>
      )}
      {/* Product Details Popup */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("products.details")}</DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <>
              {/* Company Details */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>{t("checkout.fields.company")}:</strong>{" "}
                  {selectedProduct.company.CompanyNr}
                </Typography>
              </Box>
              {/* Product Name */}
              <Typography variant="h6" gutterBottom>
                {selectedProduct.unitName}
              </Typography>

              {/* Product Notice */}
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {selectedProduct.notice || t("orders.fields.noNotes")}
              </Typography>

              {/* Product Price */}
              <Typography variant="body1" gutterBottom>
                <strong>{t("item.fields.price")}:</strong>{" "}
                {selectedProduct.unitPrice} {t("checkout.summary.currency")}
              </Typography>

              {/* Product Quantity */}
              <Typography variant="body1" gutterBottom>
                <strong>{t("checkout.fields.quantity")}:</strong>{" "}
                {selectedProduct.quantity}
              </Typography>

              {/* Product Image */}
              {selectedProduct.image ? (
                <Box
                  component="img"
                  src={selectedProduct.image}
                  alt={selectedProduct.unitName}
                  sx={{ width: "100%", borderRadius: 2, mt: 2 }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t("images.noImage1")}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            className="!text-gray-200 !px-3 !bg-zinc-600"
          >
            {t("actions.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarkteComponent;
